// src/features/portfolio-optimization/components/ActiveTiltWaterfall.tsx
//
// Zone 2 — the Institutional "Tilt" (Wealth Manager View).
//
// A diverging horizontal bar chart that decomposes the active book into its
// OVER- and UNDER-weights versus a naive equal-weight benchmark (1/N across
// the holdings the optimiser kept). Green bars (right) = the optimiser is
// leaning INTO a name; red bars (left) = it is leaning AWAY. This is the
// "where is my edge coming from" view a wealth manager reads first.
//
// We use equal-weight as the benchmark because the matrix payload carries no
// external benchmark weights; it is the most honest, self-contained baseline
// and is labelled as such in the header.

import {
    Bar,
    BarChart,
    Cell,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import type { PortfolioAsset } from '../api';

interface ActiveTiltWaterfallProps {
    holdings: PortfolioAsset[];
    /** Cap the chart to the N largest absolute tilts to stay readable. */
    maxBars?: number;
}

interface TiltDatum {
    symbol: string;
    /** Active tilt in percentage points (target% − equalWeight%). */
    tilt: number;
    target: number;
    benchmark: number;
}

const TiltTooltip = ({
    active,
    payload,
}: {
    active?: boolean;
    payload?: { payload: TiltDatum }[];
}) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const over = d.tilt >= 0;
    return (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs shadow-lg">
            <p className="font-bold text-slate-900 dark:text-white">{d.symbol}</p>
            <p
                className={`font-mono font-bold ${
                    over
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-rose-600 dark:text-rose-400'
                }`}
            >
                {over ? 'Overweight ' : 'Underweight '}
                {over ? '+' : ''}
                {d.tilt.toFixed(2)} pp
            </p>
            <p className="font-mono text-slate-500 dark:text-slate-400">
                Target {d.target.toFixed(2)}% · Benchmark {d.benchmark.toFixed(2)}%
            </p>
        </div>
    );
};

export const ActiveTiltWaterfall = ({
    holdings,
    maxBars = 14,
}: ActiveTiltWaterfallProps) => {
    if (!holdings.length) {
        return <p className="muted text-sm">No active tilts to display.</p>;
    }

    const benchmark = 100 / holdings.length; // equal-weight, in %
    const data: TiltDatum[] = holdings
        .map((h) => {
            const target = h.target_weight * 100;
            return {
                symbol: h.symbol,
                tilt: Number((target - benchmark).toFixed(2)),
                target: Number(target.toFixed(2)),
                benchmark: Number(benchmark.toFixed(2)),
            };
        })
        .sort((a, b) => Math.abs(b.tilt) - Math.abs(a.tilt))
        .slice(0, maxBars)
        .sort((a, b) => b.tilt - a.tilt);

    const maxAbs = Math.max(...data.map((d) => Math.abs(d.tilt)), 0.5);
    const chartHeight = Math.max(data.length * 30, 160);

    return (
        <div className="flex flex-col">
            <p className="text-xs text-slate-400 dark:text-slate-500 mb-3">
                Target weight vs equal-weight benchmark ({benchmark.toFixed(2)}% / name)
            </p>
            <div style={{ height: chartHeight }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={data}
                        layout="vertical"
                        margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
                    >
                        <XAxis
                            type="number"
                            domain={[-maxAbs, maxAbs]}
                            tickFormatter={(v: number) => `${v > 0 ? '+' : ''}${v}`}
                            tick={{ fontSize: 11, fill: '#94a3b8' }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            type="category"
                            dataKey="symbol"
                            width={56}
                            tick={{ fontSize: 11, fill: '#94a3b8', fontWeight: 700 }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <Tooltip
                            content={<TiltTooltip />}
                            cursor={{ fill: 'rgba(148,163,184,0.1)' }}
                        />
                        <ReferenceLine x={0} stroke="#94a3b8" strokeWidth={1} />
                        <Bar dataKey="tilt" radius={[2, 2, 2, 2]} barSize={16}>
                            {data.map((d) => (
                                <Cell
                                    key={d.symbol}
                                    fill={d.tilt >= 0 ? '#10b981' : '#f43f5e'}
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default ActiveTiltWaterfall;
